/*
 * Account screens (cloud mode only): session check before anything is shown, magic-link sign-in,
 * signing in again after an expired session, log out / switch account, and moving data saved in this
 * browser before signing in into the account.
 */
import type { Session } from "@supabase/auth-js";
import { accessTokenFor, authClient, initAuth, onAuthChange, sendMagicLink, signOutHere, verifyCode, wasExplicitLogout } from "../cloud/auth";
import type { CloudConfig } from "../cloud/config";
import { FAIL_MESSAGES, LibraryStore } from "../data/store";
import { esc } from "../domain/format";
import { cloneLibrary, totals } from "../domain/library";
import { applyMerge, planImport } from "../domain/merge";
import { CloudRepository, SignedOutRepository } from "../storage/cloud-repository";
import { failReasonOf } from "../storage/errors";
import { LocalStorageRepository } from "../storage/local-storage";
import { removeSampleData } from "../sample/sample-data";
import { topBar } from "./app";
import { ctx } from "./context";
import { failNotice, showFail } from "./failure";
import { ic } from "./icons";
import { refreshSafety, resetSafety, SAFETY_FAILED_MESSAGE } from "./import-export";
import { closeSheet, head, openSheet, toast } from "./shell";

let cfg: CloudConfig;
/** bumped on every sign-in, log out and switch; work started for an older value is dropped */
let epoch = 0;
const login = { step: "email" as "email" | "sent", email: "", error: "", info: "", cooldownUntil: 0, busy: false };
const COOLDOWN_MS = 60_000;

/* ---------- boot and session events ---------- */

export async function bootCloud(c: CloudConfig) {
  cfg = c;
  ctx.mode = "cloud";
  ctx.store = new LibraryStore(new SignedOutRepository());
  ctx.V.view = "loading"; ctx.render();
  let session: Session | null = null, linkError: string | null = null;
  try { ({ session, linkError } = await initAuth(c)); }
  catch { linkError = "ตรวจสถานะการเข้าสู่ระบบไม่ได้ ลองเปิดหน้าใหม่"; }
  if (session) void openAccount(session);
  else { login.error = linkError ?? ""; toLogin(); }

  onAuthChange((event, s) => {
    if (s && (!ctx.account || s.user.id !== ctx.account.id)) { void openAccount(s); return; } // signed in, or another account took over
    if (s && ctx.account) {
      if (s.user.email) ctx.account.email = s.user.email;
      if (ctx.sessionLost) { ctx.sessionLost = false; closeAuthLayer(); ctx.render(); toast("เข้าสู่ระบบอีกครั้งแล้ว กดบันทึกได้เลย"); }
      return;
    }
    if (event === "SIGNED_OUT" && ctx.account) {
      if (wasExplicitLogout()) { ++epoch; clearAccount(); resetLogin(); toLogin("ออกจากระบบแล้ว"); } // logged out in another tab
      else { ctx.sessionLost = true; ctx.render(); } // expired: keep the screen and any open form
    }
  });
}

/** Shows the given account: everything of the previous account is removed first. */
async function openAccount(s: Session) {
  const my = ++epoch;
  clearAccount();
  const email = s.user.email ?? "";
  ctx.account = { id: s.user.id, email };
  const store = new LibraryStore(new CloudRepository(cfg, s.user.id, email, accessTokenFor));
  ctx.store = store;
  ctx.V.view = "loading"; ctx.render();
  const r = await store.init();
  if (my !== epoch) return;
  await refreshSafety();
  if (my !== epoch) return;
  await refreshLocalOffer();
  if (my !== epoch) return;
  resetLogin();
  if (r.status === "error") loadError = r.error;
  ctx.V.view = r.status === "ok" ? "home" : r.status === "empty" ? "welcome" : "load-error";
  ctx.render(); scrollTo(0, 0);
}

/** Removes the current account's data from memory and from the screen, and stops its pending requests. */
function clearAccount() {
  ctx.store?.dispose();
  ctx.store = new LibraryStore(new SignedOutRepository());
  ctx.account = null; ctx.sessionLost = false; ctx.localOffer = null;
  resetSafety();
  closeSheet(); closeAuthLayer();
  document.querySelector(".toast")?.remove();
  Object.assign(ctx.V, { view: "loading", catId: null, edit: false });
}

function resetLogin() { Object.assign(login, { step: "email", email: "", error: "", info: "", busy: false }); }
function toLogin(msg?: string) { ctx.V.view = "login"; ctx.render(); scrollTo(0, 0); if (msg) toast(msg); }

export async function logout(switching: boolean) {
  ++epoch;
  clearAccount();
  resetLogin();
  login.info = switching ? "ออกจากบัญชีเดิมแล้ว เข้าสู่ระบบด้วยบัญชีอื่นได้เลย" : "";
  toLogin(switching ? "" : "ออกจากระบบแล้ว");
  await signOutHere();
}

/* ---------- screens: checking session, sign in, load error ---------- */

let loadError = "";
const keysArt = `<div class="welcome-keys" aria-hidden="true"><span class="key" style="--g:#A78BFA"><span class="face"></span>${ic("link")}</span><span class="key" style="--g:#34D399"><span class="face"></span>${ic("note")}</span><span class="key" style="--g:#38BDF8"><span class="face"></span>${ic("grid")}</span></div>`;

export function renderAccountView($app: HTMLElement, view: "loading" | "login" | "load-error") {
  if (view === "loading") {
    $app.innerHTML = topBar() + `<section class="welcome" aria-busy="true">${keysArt}<h2>MEMOLINK</h2><p id="acct-loading">กำลังตรวจบัญชีและโหลดข้อมูล…</p></section>`;
    return;
  }
  if (view === "load-error") {
    $app.innerHTML = topBar() + `<section class="welcome">${keysArt}<h2>โหลดข้อมูลไม่ได้</h2>
      <p class="note" id="load-error-text"><span class="tag bad">แจ้งเตือน</span> ${esc(loadError)} ข้อมูลในบัญชียังอยู่ ยังไม่มีอะไรถูกเปลี่ยน</p>
      <div class="stack"><button class="btn primary" data-do="load-retry" id="btn-load-retry">${ic("left")}ลองอีกครั้ง</button>
      <button class="btn" data-do="acct-logout" id="btn-load-logout">${ic("x")}ออกจากระบบ</button></div></section>`;
    return;
  }
  const local = localCountText();
  $app.innerHTML = topBar() + `<section class="welcome login-root" id="login-page">${keysArt}
      <h2>เข้าสู่ระบบ MEMOLINK</h2>
      <p>ใส่อีเมลแล้วเราจะส่งลิงก์เข้าสู่ระบบให้ ไม่ต้องตั้งรหัสผ่าน ข้อมูลเก็บบน Cloud ในบัญชีของคุณ เปิดเครื่องไหนก็เห็นข้อมูลเดียวกัน</p>
      ${login.info ? `<p class="note">${esc(login.info)}</p>` : ""}
      ${loginFormHTML()}
      ${local ? `<p class="note" style="margin-top:14px" id="login-local-note">เบราว์เซอร์นี้มีข้อมูลที่เก็บไว้ก่อนมีบัญชี (${local}) ข้อมูลยังอยู่ในเครื่อง ไม่ถูกอัปโหลดเอง หลังเข้าสู่ระบบเลือกย้ายเข้าบัญชีได้</p>` : ""}
    </section>`;
}

function loginFormHTML(lockedEmail?: string) {
  const err = `<p class="err" id="lg-err" role="alert" ${login.error ? "" : "hidden"}>${esc(login.error)}</p>`;
  if (login.step === "email") return `
    <div class="field"><label for="lg-email">อีเมล</label><input class="inp" id="lg-email" type="email" inputmode="email" autocomplete="email" autocapitalize="off" spellcheck="false" value="${esc(lockedEmail ?? login.email)}" ${lockedEmail ? "readonly" : ""} placeholder="you@example.com"><span class="err" id="lg-email-err" hidden>กรุณาใส่อีเมลให้ถูกต้อง</span></div>
    ${err}
    <div class="stack"><button class="btn primary" data-do="lg-send" id="btn-send-link">${ic("mail")}ส่งลิงก์เข้าสู่ระบบ</button></div>`;
  return `
    <p class="note" id="lg-sent"><span class="tag okk">ส่งแล้ว</span> ส่งลิงก์ไปที่ <b>${esc(login.email)}</b> แล้ว เปิดอีเมลบนเครื่องนี้แล้วแตะลิงก์ หรือพิมพ์รหัส 6 หลักจากอีเมลด้านล่าง</p>
    <div class="field"><label for="lg-code">รหัสจากอีเมล</label><input class="inp" id="lg-code" inputmode="numeric" autocomplete="one-time-code" maxlength="10" placeholder="123456"></div>
    ${err}
    <div class="stack"><button class="btn primary" data-do="lg-verify" id="btn-verify">${ic("check")}ยืนยันรหัส</button>
      <button class="btn" data-do="lg-resend" id="btn-resend">${ic("mail")}ส่งลิงก์อีกครั้ง</button>
      ${lockedEmail ? "" : `<button class="btn ghost" data-do="lg-change" id="btn-change-email">${ic("left")}ใช้อีเมลอื่น</button>`}</div>`;
}

function localCountText() {
  const o = ctx.localOffer ?? peekLocal();
  if (!o) return "";
  const t = totals(o.library);
  return `${t.workspaces} สถานที่ · ${t.categories} หมวด · ${t.actions} Action`;
}
/** cheap read for the sign-in screen; nothing is written */
function peekLocal(): { library: import("../domain/types").Library } | null {
  try {
    const raw = localStorage.getItem("memolink-app-v2"); if (!raw) return null;
    const lib = JSON.parse(raw)?.library;
    return lib && Array.isArray(lib.workspaces) && Array.isArray(lib.categories) && Array.isArray(lib.actions) && (lib.workspaces.length + lib.categories.length + lib.actions.length) ? { library: lib } : null;
  } catch { return null; }
}

/* ---------- sign in again after the session expired (over any open form) ---------- */

const $auth = () => document.getElementById("auth-layer");
export function openRelogin() {
  if (!ctx.account) return;
  if (login.step === "sent" && login.email !== ctx.account.email) resetLogin();
  login.email = ctx.account.email;
  let el = $auth();
  if (!el) { el = document.createElement("div"); el.id = "auth-layer"; document.body.appendChild(el); }
  el.innerHTML = `<div class="scrim auth-scrim"><div class="sheet login-root" role="dialog" aria-modal="true" id="relogin-sheet"><div class="grab"></div>
    <div class="sh-head"><h3>เข้าสู่ระบบอีกครั้ง</h3><button class="iconbtn" data-do="auth-close" aria-label="ปิด">${ic("x")}</button></div>
    <p class="note">เซสชันหมดอายุ ค่าที่กรอกไว้และข้อมูลเดิมยังอยู่ เข้าสู่ระบบด้วยบัญชีเดิมแล้วกดบันทึกอีกครั้ง ลิงก์จากอีเมลจะเปิดแท็บใหม่ หน้านี้จะกลับมาบันทึกได้เอง</p>
    ${loginFormHTML(ctx.account.email)}</div></div>`;
}
export function closeAuthLayer() { $auth()?.remove(); }

function rerenderLogin(root: HTMLElement) {
  if (root.id === "relogin-sheet") openRelogin(); else ctx.render();
}

async function sendLink(root: HTMLElement, resend: boolean) {
  if (login.busy) return;
  const input = root.querySelector<HTMLInputElement>("#lg-email");
  const email = resend ? login.email : (input?.value ?? "").trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { const e = root.querySelector<HTMLElement>("#lg-email-err"); if (e) e.hidden = false; input?.focus(); return; }
  const wait = Math.ceil((login.cooldownUntil - Date.now()) / 1000);
  if (email === login.email && wait > 0) { login.error = `เพิ่งส่งไป รออีก ${wait} วินาทีแล้วส่งอีกครั้ง`; rerenderLogin(root); return; }
  login.busy = true;
  root.querySelectorAll<HTMLButtonElement>("button[data-do^=lg-]").forEach(b => b.disabled = true);
  const r = await sendMagicLink(email);
  login.busy = false;
  if (!r.ok) { login.error = r.message; rerenderLogin(root); return; }
  Object.assign(login, { email, step: "sent", error: "", cooldownUntil: Date.now() + COOLDOWN_MS });
  rerenderLogin(root);
  toast("ส่งลิงก์เข้าสู่ระบบแล้ว");
}

async function verify(root: HTMLElement) {
  if (login.busy) return;
  const code = (root.querySelector<HTMLInputElement>("#lg-code")?.value ?? "").replace(/\D/g, "");
  if (code.length < 6) { login.error = "ใส่รหัสตัวเลขจากอีเมล"; rerenderLogin(root); return; }
  login.busy = true;
  root.querySelectorAll<HTMLButtonElement>("button[data-do^=lg-]").forEach(b => b.disabled = true);
  const r = await verifyCode(login.email, code);
  login.busy = false;
  if (!r.ok) { login.error = r.message; rerenderLogin(root); return; }
  // the auth event opens the account (or, for the same account, closes this sheet)
}

/* ---------- data saved in this browser before signing in ---------- */

const flagKey = (k: string) => `memolink-cloud:${k}:${ctx.account?.id}`;
const flag = (k: string) => { try { return !!localStorage.getItem(flagKey(k)); } catch { return false; } };
const setFlag = (k: string) => { try { localStorage.setItem(flagKey(k), new Date().toISOString()); } catch { /* only hides the banner */ } };

/** Reads the Queue 2 local library (read only; it is never uploaded or deleted without the person's choice). */
export async function refreshLocalOffer() {
  ctx.localOffer = null;
  const r = await new LocalStorageRepository().load();
  if (r.status !== "ok") return;
  const lib = r.library;
  const all = lib.workspaces.length + lib.categories.length + lib.actions.length;
  const sample = [...lib.workspaces, ...lib.categories, ...lib.actions].filter(x => x.sample).length;
  if (all) ctx.localOffer = { library: lib, own: all - sample, sample };
}

/** banners shown under the top bar in cloud mode */
export function bannersHTML(): string {
  if (ctx.mode !== "cloud" || !ctx.account) return "";
  let html = "";
  if (ctx.sessionLost) html += `<div class="acct-banner bad" id="session-banner" role="status"><span>เซสชันหมดอายุ ข้อมูลบนหน้าจอยังอยู่ แต่จะบันทึกได้เมื่อเข้าสู่ระบบอีกครั้ง</span><button class="btn" data-do="relogin" id="btn-relogin">${ic("user")}เข้าสู่ระบบอีกครั้ง</button></div>`;
  if (ctx.V.view === "home" && ctx.localOffer && !flag("migrated") && !flag("offer-dismissed"))
    html += `<div class="acct-banner" id="local-offer"><span>เบราว์เซอร์นี้มีข้อมูลที่เก็บไว้ก่อนเข้าสู่ระบบ (${localCountText()}) ยังไม่ได้อยู่ในบัญชี</span><div class="row"><button class="btn ghost" data-do="migrate-dismiss" id="btn-offer-dismiss">ไม่ต้อง</button><button class="btn primary" data-do="migrate-open" id="btn-offer-open">ดูและย้าย</button></div></div>`;
  return html;
}

/** Summary first; nothing is uploaded until "move" is pressed. Sample records stay out unless ticked. */
export function openMigrate(withSample = false) {
  const off = ctx.localOffer, acct = ctx.account;
  if (!off || !acct) return;
  const source = withSample ? cloneLibrary(off.library) : removeSampleData(off.library).library;
  const target = ctx.store.lib;
  const plan = planImport(target, source);
  const addN = plan.toAdd.workspaces + plan.toAdd.categories + plan.toAdd.actions;
  const lt = totals(off.library);
  const html = head("ย้ายข้อมูลในเครื่องนี้เข้าบัญชี")
    + `<p class="note">คัดลอกข้อมูลที่เก็บในเบราว์เซอร์นี้เข้าบัญชี <b id="mg-account">${esc(acct.email)}</b> บน Cloud ข้อมูลในเครื่องไม่ถูกลบ</p>`
    + `<div class="panel"><dl class="kv" id="mg-summary">
        <dt>ในเครื่องนี้</dt><dd>${lt.workspaces} สถานที่ · ${lt.categories} หมวด · ${lt.actions} Action (ของคุณ ${off.own} · ตัวอย่าง ${off.sample})</dd>
        <dt>จะเพิ่มเข้าบัญชี</dt><dd id="mg-add">${plan.toAdd.workspaces} สถานที่ · ${plan.toAdd.categories} หมวด · ${plan.toAdd.actions} Action</dd>
        <dt>มีในบัญชีแล้ว</dt><dd id="mg-same">${plan.identical} รายการ (ข้าม ไม่เพิ่มซ้ำ)</dd>
        <dt>ID ชนกัน</dt><dd id="mg-conf">${plan.conflicts.length ? `${plan.conflicts.length} รายการ เก็บของในบัญชีไว้ ไม่เขียนทับ` : "ไม่มี"}</dd>
      </dl></div>`
    + (off.sample ? `<label class="opt" style="margin-top:12px"><input type="checkbox" id="mg-sample" ${withSample ? "checked" : ""}><span class="ot"><b>รวมข้อมูลตัวอย่าง (${off.sample} รายการ)</b><small>ปกติไม่ย้ายข้อมูลตัวอย่างขึ้น Cloud หมวด/สถานที่ตัวอย่างที่มีรายการของคุณอยู่จะย้ายไปด้วยเพื่อเก็บรายการนั้น</small></span></label>` : "")
    + (plan.blocked.length ? `<p class="err" id="mg-blocked" style="margin:12px 0">${plan.blocked.map(esc).join("<br>")}<br>ย้ายไม่ได้ ข้อมูลในบัญชีไม่ถูกเปลี่ยน</p>` : "")
    + `<p class="err" id="mg-err" role="alert" style="margin:14px 0 0" hidden></p>`
    + `<div class="stack" style="margin-top:14px"><button class="btn primary" id="mg-confirm" ${plan.blocked.length ? "disabled" : ""}>${ic("check")}${addN ? `ย้ายเข้าบัญชีนี้ (${addN} รายการ)` : "ไม่มีข้อมูลใหม่ ปิด"}</button><button class="btn ghost" data-do="close">ยังไม่ย้าย</button></div>`;
  openSheet(html, sh => {
    sh.querySelector<HTMLInputElement>("#mg-sample")?.addEventListener("change", e => openMigrate((e.target as HTMLInputElement).checked));
    const btn = sh.querySelector<HTMLButtonElement>("#mg-confirm")!, errEl = sh.querySelector<HTMLElement>("#mg-err")!;
    let busy = false;
    btn.addEventListener("click", async () => {
      if (busy || btn.disabled) return;
      if (!addN) { closeSheet(); setFlag("migrated"); ctx.render(); toast("ข้อมูลในเครื่องนี้อยู่ในบัญชีครบแล้ว"); return; }
      busy = true; errEl.hidden = true;
      try {
        const store = ctx.store;
        if (store.started) {
          try { await store.repo.saveSafetyCopy(store.lib, "ก่อนย้ายข้อมูลจากเครื่องนี้"); }
          catch (e) { const reason = failReasonOf(e); showFail(errEl, { ok: false, reason, message: reason === "storage" ? SAFETY_FAILED_MESSAGE : FAIL_MESSAGES[reason] }, () => openMigrate(withSample)); return; }
        }
        const next = applyMerge(store.lib, plan, "keep-local");
        next.settings.openWith = store.lib.settings.openWith;
        if (!next.workspaces.some(w => w.id === next.settings.activeWorkspaceId)) next.settings.activeWorkspaceId = next.workspaces[0]?.id ?? null;
        const r = await store.replace(next);
        if (!r.ok) { showFail(errEl, r, () => openMigrate(withSample)); return; }
        await refreshSafety();
        setFlag("migrated");
        closeSheet();
        Object.assign(ctx.V, { view: "home", catId: null, edit: false }); ctx.render(); scrollTo(0, 0);
        toast(`ย้ายเข้าบัญชีแล้ว เพิ่ม ${addN} รายการ ข้อมูลในเครื่องยังอยู่`);
      } finally { busy = false; }
    });
  });
}

/* ---------- settings section ---------- */

export function accountSettingsHTML(): string {
  if (ctx.mode !== "cloud" || !ctx.account) return "";
  const o = ctx.localOffer;
  return `<div class="set" id="set-account"><h2>${ic("user")}บัญชี</h2>
      <div class="panel"><dl class="kv"><dt>เข้าสู่ระบบเป็น</dt><dd id="acct-email">${esc(ctx.account.email)}</dd><dt>ข้อมูลอยู่ที่</dt><dd>Cloud ของบัญชีนี้ บัญชีอื่นเปิดดูหรือแก้ไม่ได้</dd></dl></div>
      <div class="stack" style="margin-top:10px">
        <button class="btn" data-do="acct-reload" id="btn-acct-reload">${ic("left")}โหลดข้อมูลล่าสุดจาก Cloud</button>
        <button class="btn" data-do="acct-switch" id="btn-switch">${ic("users")}สลับบัญชี</button>
        <button class="btn" data-do="acct-logout" id="btn-logout">${ic("x")}ออกจากระบบ</button>
      </div></div>`
    + (o ? `<div class="set" id="set-local"><h2>${ic("folder")}ข้อมูลในเครื่องนี้ (ก่อนเข้าสู่ระบบ)</h2>
      <p>เบราว์เซอร์นี้มีข้อมูลที่เก็บไว้ก่อนมีบัญชี ${localCountText()} (ของคุณ ${o.own} · ตัวอย่าง ${o.sample}) ยังอยู่ในเครื่องตามเดิม ไม่ถูกอัปโหลดเอง${flag("migrated") ? " · ย้ายเข้าบัญชีแล้วอย่างน้อย 1 ครั้ง" : ""}</p>
      <button class="btn" data-do="migrate-open" id="btn-migrate" style="width:100%">${ic("check")}ดูสรุปและย้ายเข้าบัญชีนี้</button></div>` : "");
}

/* ---------- clicks and keys ---------- */

export function handleAccountAction(act: string, el: HTMLElement): boolean {
  const root = el.closest<HTMLElement>(".login-root");
  switch (act) {
    case "lg-send": if (root) void sendLink(root, false); return true;
    case "lg-resend": if (root) void sendLink(root, true); return true;
    case "lg-verify": if (root) void verify(root); return true;
    case "lg-change": if (root) { login.step = "email"; login.error = ""; rerenderLogin(root); } return true;
    case "relogin": openRelogin(); return true;
    case "auth-close": closeAuthLayer(); return true;
    case "acct-logout": void logout(false); return true;
    case "acct-switch": void logout(true); return true;
    case "acct-reload":
      void ctx.store.reload().then(r => {
        if (!r.ok) { failNotice(r); return; }
        if (!ctx.store.started) ctx.V.view = "welcome";
        ctx.render(); toast("โหลดข้อมูลล่าสุดแล้ว");
      });
      return true;
    case "load-retry":
      void authClient().auth.getSession().then(({ data }) => { if (data.session) void openAccount(data.session); else { ++epoch; clearAccount(); toLogin(); } });
      return true;
    case "migrate-open": openMigrate(false); return true;
    case "migrate-dismiss": setFlag("offer-dismissed"); ctx.render(); toast("ซ่อนแล้ว ย้ายภายหลังได้ที่ ตั้งค่า"); return true;
  }
  return false;
}

document.addEventListener("keydown", e => {
  if (e.key !== "Enter") return;
  const t = e.target as HTMLElement, root = t.closest<HTMLElement>(".login-root");
  if (!root) return;
  if (t.id === "lg-email") { e.preventDefault(); void sendLink(root, false); }
  if (t.id === "lg-code") { e.preventDefault(); void verify(root); }
});
